import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import i18n from 'i18n';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams, useNavigate } from 'react-router';
import { connect, ConnectedProps } from 'react-redux';
import { IGlobalReducerState } from 'Store/Base/interface/IGlobalReducerState';
import { useQueryUniqueObra } from 'Hooks/obras/useQueryUniqueObra';
import ProgressRenderBar from 'Pages/ListagemArquivos/Modals/ProgressRenderBar';
import useFederatedViewer from './useFederatedViewer';
import FileDestination from './Modals/FileDestination';
import CTAViewerUpgrade from './Modals/CTAViewerUpgrade';
import CTAMultipleModels from './Modals/CTAMultipleModels';
import ViewerSignalR from 'Components/UI/SignalR/viewer';
import TabsShell, { PathActiveEnum } from 'Components/UI/TabsShell';
import FederatedDrawer from './components/FederatedDrawer';
import SearchViewerInput from 'Components/UI/SearchViewerInput';
import LoadingDrawer from 'Components/UI/CustomLoading/LoadingDrawer';
import Models from './components/Models';
import EmptyViewer from './components/EmptyViewer';
import ViewerLoadingOverlay from './components/ViewerLoadingOverlay';
import EmptyModels from './components/EmptyModels';
import VideoEx from 'Components/VideoEx';
import CTAUpgradeAccessViewer from './components/CTAUpgradeAccessViewer';
import ModelsViewpoints from './components/ModelsViewpoints';
import ModelsPranchas from './components/ModelsPranchas';
import ActivitiesList from './components/ActivitiesList';
import useShowCreateActivityViewer from './hooks/useShowCreateActivityViewer';
import ShowCreateActivity from 'Pages/Activities/Modals/ShowCreateActivity';
import InternalViewer from 'Components/UI/InternalViewer';
import ModelsViews from './components/ModelsViews';
import toastHandler from 'Utils/toastHandler';
import { ActivitiesApi } from 'Data/ActivitiesApi';
import { ActivitiesActions } from 'Store/Activities/Activities.actions';
import { FederatedViewerActions } from 'Store/FederatedViewer/FederatedViewer.actions';
import { BimSelection, BimTreeNode, BimViewerHandle } from 'Data/interfaces/BimViewer/IBimViewerPort';
import { IIfcModelTreeEntry } from './components/IfcModelTree';
import { IInitialViewpoint, IViewpointBimFile, ITopicPoint } from 'Data/interfaces/Activities/IViewpointData';
import { useFeatureFlag } from 'Hooks/useFeatureFlag';
import ActivitiesDemo from './components/ActivitiesDemo';
import { useCoordly3DViewerFeature, COORDLY_3D_VIEWER_EXTENSION } from 'Utils/ViewerUtils';
import { isWebGPUAvailable } from 'Utils/webgpu';
import { Mixpanel } from 'Utils/MixPanel';
import { FederatedIfcViewer } from './components/FederatedIfcViewer';
import { isIfcUrn, resolveUrnToFile } from './federationUrn';
import styles from './FederadoViewer.module.scss';

const FederatedViewer: FC<Props> = ({
  isLoading,
  urns,
  userInfo,
  accessViewerTab,
  modelsTree,
  viewerListDisciplines,
  loadingDiscipline,
  loadingFiles,
  hideNodes,
  selectedNode,
  userLimitsActive,
  views,
  models,
  pranchas,
  viewpoints,
}) => {
  const { t } = useTranslation('federatedViewer');
  const activityViewpointFeatureFlag = useFeatureFlag('activity-viewpoint-feature');
  const activityViewpointFeature = activityViewpointFeatureFlag.enabled &&
    activityViewpointFeatureFlag.value === 'test';

  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [createActivityHandler, setCreateActivityHandler] = useState<(() => void) | null>(null);
  const [viewpointRequest, setViewpointRequest] = useState<{ topicId: string; viewpointId: string } | null>(null);
  const [pendingViewpoint, setPendingViewpoint] = useState<IInitialViewpoint | null>(null);
  const [pendingTopicPoints, setPendingTopicPoints] = useState<ITopicPoint[] | null>(null);
  const [restorerReady, setRestorerReady] = useState(false);
  const [viewpointLoading, setViewpointLoading] = useState(false);
  // Urn que o motor IFC está carregando. Enquanto houver um, a lista trava os
  // switches — mesmo papel do `loadUrn` no caminho da Autodesk.
  const [ifcLoadingUrn, setIfcLoadingUrn] = useState<string | null>(null);
  // Árvore espacial por urn e o handle do motor, que a lista usa para desenhar a
  // hierarquia de cada modelo IFC e selecionar na cena a partir dela.
  const [ifcTrees, setIfcTrees] = useState<Record<string, IIfcModelTreeEntry>>({});
  // Ref, não estado: o `IfcModelTree` só precisa ler o handle na hora de agir,
  // e guardá-lo em estado re-renderizaria a lista inteira a cada montagem.
  const ifcHandleRef = useRef<BimViewerHandle | null>(null);

  const handleIfcModelTree = useCallback((
    urn: string,
    tree: BimTreeNode[],
    modelIndex?: number,
  ) => {
    setIfcTrees((current) => {
      // Árvore vazia = modelo saiu da cena; tirar a chave evita a lista crescer
      // com urns que não estão mais ligados.
      if (tree.length === 0) {
        if (!(urn in current)) { return current; }
        const next = { ...current };
        delete next[urn];
        return next;
      }
      return { ...current, [urn]: { tree, modelIndex } };
    });
  }, []);

  // A árvore acompanha o modelo. O `FederatedIfcViewer` avisa quando tira um da
  // cena, mas ele desmonta junto com o ÚLTIMO urn — nesse caminho o aviso nunca
  // chega (o efeito de reconciliação não roda em componente que já saiu) e a
  // árvore ficava na lista apontando para uma cena que não existe mais. Aqui a
  // fonte da verdade é `urns`, que não depende de quem está montado.
  useEffect(() => {
    setIfcTrees((current) => {
      const stale = Object.keys(current).filter((urn) => !urns.includes(urn));
      if (stale.length === 0) { return current; }
      const next = { ...current };
      for (const urn of stale) { delete next[urn]; }
      return next;
    });
  }, [urns]);

  // Seleção da cena IFC → árvores. Assinatura, e não estado: o motor emite de
  // forma SÍNCRONA dentro do clique, e é essa ordem que faz o clique num nó
  // contêiner funcionar — ele limpa o destaque no 3D (evento de seleção vazia) e
  // logo depois marca o nó. Passando por estado, o vazio chegaria num render
  // seguinte e apagaria a marcação que o próprio clique acabou de fazer.
  const ifcSelectionListeners = useRef<Set<(selection: BimSelection) => void>>(new Set());

  const subscribeIfcSelection = useCallback((listener: (selection: BimSelection) => void) => {
    ifcSelectionListeners.current.add(listener);
    return () => { ifcSelectionListeners.current.delete(listener); };
  }, []);

  const handleIfcSelectionChanged = useCallback((selection: BimSelection) => {
    ifcSelectionListeners.current.forEach((listener) => listener(selection));
  }, []);
  const viewpointRestorerRef = useRef<(viewpoint: IInitialViewpoint, topicPoints?: ITopicPoint[]) => void>();
  // Urns que a Autodesk não conseguiu abrir nesta sessão. Sem essa memória, todo
  // caminho que repõe modelos (link de Atividade, retomada de urns pendentes)
  // religa o modelo quebrado e cai no mesmo erro em laço.
  const failedUrnsRef = useRef<Set<string>>(new Set());

  const handleModelLoadError = useCallback((urn: string) => {
    failedUrnsRef.current.add(urn);
    // O erro já vira toast no viewer; manter o overlay de loading por cima até o
    // teto de tempo só esconderia a mensagem.
    setViewpointLoading(false);
  }, []);

  const normalizeUrn = useCallback((rawUrn: string | undefined | null): string | null => {
    if (!rawUrn) {
      return null;
    }
    return rawUrn.startsWith('urn:') ? rawUrn : `urn:${rawUrn}`;
  }, []);

  const stripUrnPrefix = useCallback((rawUrn: string | undefined | null): string | null => {
    if (!rawUrn) {
      return null;
    }
    return rawUrn.replace(/^urn:/i, '');
  }, []);

  const {
    showActivityModal,
    selectedActivity,
    isLoading: activityLoading,
    isLoadingUpdate,
    isLoadingDelete,
    topicStatus,
    topicStages,
    topicPoints,
    topicCommunicationType,
    visibilityOptions,
    priorityOptions,
    priorityNoBgOptions,
    statusOptions,
    typeOptions,
    stageOptions,
    pointsOptions,
    communicationOptions,
    userOptions,
    disciplineOptions,
    disciplines,
    updateStatus,
    getOld,
    newActivityGuid,
    editStage,
    allowOnlyStagesLinkedToDisciplinesInTopics,
    createStageLoading,
    handleOpenActivity,
    handleCloseActivity,
    handleOpenShowActivity,
  } = useShowCreateActivityViewer({
    csId: Number(params.constructionSiteId),
    userInfo,
  });
  const { data: obra } = useQueryUniqueObra({
    csId: Number(params.constructionSiteId),
    navigate,
  });

  // Motor da sessão federada: precisa ser resolvido ANTES do useFederatedViewer,
  // que decide se o IFC entra na conversão automática da Autodesk.
  const { isCoordly3DViewerEnabled } = useCoordly3DViewerFeature();
  const [webgpuOk, setWebgpuOk] = useState<boolean | null>(null);

  useEffect(() => {
    if (isCoordly3DViewerEnabled) { isWebGPUAvailable().then(setWebgpuOk); }
  }, [isCoordly3DViewerEnabled]);

  // IFC servido pelo nosso motor: sai da fila da Autodesk e o switch não depende
  // do urn. Sem flag ou sem WebGPU o fluxo Autodesk segue intocado.
  const ifcEngineReady = isCoordly3DViewerEnabled && webgpuOk === true;

  // Conversor automático pausado até segunda ordem enquanto a flag estiver ativa
  // (decisão do PO, 2026-07-27) — vale para TODA extensão, não só IFC.
  const blockAutoRender = isCoordly3DViewerEnabled;

  const {
    demoVideoViewer,
    activeDrawer,
    drawerFederatedVisible,
    activeDisciplineId,
    filteredEmptyDisciplines,
    childrenActiveDisciplines,
    searchDiscipline,
    searchDisciplineFiles,
    loadUrn,
    searchItens,
    childrenActiveUrnsNames,
    toggleView,
    toggleViewCamera,
    toggleHideShowNode,
    toggleSelectNode,
    togglePrancha,
    showVideo,
    showSelectUploadPath,
    upfileViewer,
    selectUploadPathFromMaleta,
    showCTAViewerUpgrade,
    showCTAMultipleModelsUpgrade,
    indexList,
    showModalProgressLimitRender,
    handleActiveList,
    closeDrawerFederated,
    showDrawerFederated,
    handleSearch,
    uploadFileViewer,
    handleMaletaFileViewer,
    handleShowCTARender,
    handleShowProgressLimitRender,
    openDiscipline,
    backDisciplines,
    handleLoadUrn,
    handleActiveUrns,
    handleOpenCTAModal,
    handleToogleNode,
    handleToogleSelectNode,
    dispatch,
    handleOpenVideoModal,
    handleCloseVideoModal,
    handleUpgradePlan,
    handleToogleView,
    handleToogleViewCamera,
    handleTooglePrancha,
    handleUploadFileViewer,
    cancelUploadFileViewer,
    setShowCTAViewerUpgrade,
    handleCloseCTAModal,
    handleCloseProgressLimitRender,
    handleClearAllToogles,
  } = useFederatedViewer({
    constructionSiteId: Number(params.constructionSiteId),
    isLoading,
    urns,
    userInfo,
    viewerListDisciplines,
    loadingDiscipline,
    blockAutoRender,
  });

  const findExtensionForUrn = useCallback(
    (urn: string): string | undefined => {
      // urn sintética (IFC sem artefato Autodesk) resolve pelo identificador.
      if (isIfcUrn(urn)) {
        return resolveUrnToFile(urn, viewerListDisciplines)?.Extension;
      }

      const urnWithoutPrefix = stripUrnPrefix(urn);
      if (!urnWithoutPrefix) {
        return undefined;
      }

      for (const discipline of viewerListDisciplines) {
        const match = discipline.FilesList?.find(
          (file) => stripUrnPrefix(file.FileInfo?.BucketFile?.Urn || '') === urnWithoutPrefix,
        );
        if (match?.Extension) {
          return match.Extension;
        }
      }
      return undefined;
    },
    [stripUrnPrefix, viewerListDisciplines],
  );

  // Motor da sessão federada: o 1º urn ativo decide. IFC (+ flag + WebGPU) → nosso
  // viewer ifc-lite; senão Autodesk (que cuida de tudo, inclusive IFC). Cena vazia
  // reseta (item 3 do gating). Deriva de urns/extensão — não muta Redux.
  const sessionEngine = useMemo<'ifc' | 'autodesk' | 'pending' | null>(() => {
    if (urns.length === 0) { return null; }
    const firstExt = (findExtensionForUrn(urns[0]) || '').toLowerCase();
    if (firstExt !== COORDLY_3D_VIEWER_EXTENSION || !isCoordly3DViewerEnabled) { return 'autodesk'; }
    if (webgpuOk === null) { return 'pending'; } // probe async ainda resolvendo
    return webgpuOk ? 'ifc' : 'autodesk';
  }, [urns, findExtensionForUrn, isCoordly3DViewerEnabled, webgpuOk]);

  // O urn abre no NOSSO motor? Não basta a urn ser sintética: um `.ifc` que já
  // passou pela conversão da Autodesk carrega urn de verdade e mesmo assim é o
  // ifc-lite quem o desenha. Pedir `loadUrn` para um urn desses trava a lista
  // inteira — quem limpa o `loadUrn` é o InternalViewer, que não monta na sessão
  // IFC, e com ele preso todo switch fica desabilitado (nem para desligar).
  //
  // Mas isso vale só quando a sessão é NOSSA: numa sessão que começou por outro
  // tipo de arquivo, o IFC segue pelo caminho antigo (a Autodesk desenha), e aí
  // o `loadUrn` é exatamente o que ele precisa.
  //
  // `webgpuOk !== false` e não `ifcEngineReady`: enquanto o probe não resolve
  // (`null`) o motor ainda pode ser o nosso, e tratar como Autodesk nesse meio
  // tempo é justamente o que prende o `loadUrn` na abertura com urns já ativos.
  const isIfcEngineUrn = useCallback(
    (urn: string) => (
      isCoordly3DViewerEnabled
      && webgpuOk !== false
      && sessionEngine !== 'autodesk'
      && (findExtensionForUrn(urn) || '').toLowerCase() === COORDLY_3D_VIEWER_EXTENSION
    ),
    [isCoordly3DViewerEnabled, webgpuOk, sessionEngine, findExtensionForUrn],
  );

  const ensureModelsLoaded = useCallback(
    (bimFiles?: IViewpointBimFile[] | null) => {
      const urnsToActivate: string[] = [];

      if (bimFiles && bimFiles.length > 0) {
        bimFiles.forEach((file) => {
          const normalized = normalizeUrn(file?.Reference || file?.Filename);
          if (normalized) {
            urnsToActivate.push(normalized);
          }
        });
      }

      if (urnsToActivate.length === 0) {
        viewerListDisciplines.forEach((discipline) => {
          discipline.FilesList?.forEach((file) => {
            const normalized = normalizeUrn(file?.FileInfo?.BucketFile?.Urn);
            if (normalized) {
              urnsToActivate.push(normalized);
            }
          });
        });
      }

      const uniqueUrns = Array.from(new Set(urnsToActivate))
        .filter((urnValue) => !failedUrnsRef.current.has(urnValue));

      uniqueUrns.forEach((urnValue) => {
        if (!urns.includes(urnValue)) {
          const extension = findExtensionForUrn(urnValue);
          if (extension) {
            handleActiveUrns(urnValue, extension);
          }
        }
      });

      const firstPendingUrn = uniqueUrns.find((urnValue) => (
        !isIfcEngineUrn(urnValue) && !models.some((model) => model.urn === urnValue)
      )) ?? null;

      if (firstPendingUrn) {
        handleLoadUrn(firstPendingUrn);
      }
    },
    [
      findExtensionForUrn, handleActiveUrns, handleLoadUrn, isIfcEngineUrn, models, normalizeUrn,
      urns, viewerListDisciplines,
    ],
  );

  const areViewpointModelsLoaded = useCallback(
    (viewpoint: IInitialViewpoint | null): boolean => {
      if (!viewpoint) { return true; }

      if (!viewpoint.BimFiles || viewpoint.BimFiles.length === 0) {
        return models.length > 0;
      }

      return viewpoint.BimFiles.every((bimFile) => {
        const reference = normalizeUrn(bimFile.Reference || bimFile.Filename);
        if (!reference) { return true; }

        return models.some((model) => (
          model.urn === reference
          || stripUrnPrefix(model.urn) === stripUrnPrefix(reference)
        ));
      });
    },
    [models, normalizeUrn, stripUrnPrefix],
  );

  // Handle create activity from drawer
  const handleCreateActivityHandlerReady = (handler: () => void) => {
    setCreateActivityHandler(() => handler);
  };

  const handleCreateActivity = () => {
    if (createActivityHandler) {
      // Use viewer's modal with viewpoint capture
      createActivityHandler();
    } else {
      // Viewer not ready yet
      toastHandler.showInfo(t('page.createActivityNotice'));
    }
  };

  const handleOpenActivityViewpoint = async (topicId: string, viewpointId: string) => {
    handleCloseActivity();

    // Update URL without page reload (allows sharing/bookmarking)
    const url = new URL(window.location.href);
    url.searchParams.set('topicId', topicId);
    url.searchParams.set('viewpointId', viewpointId);
    window.history.pushState({}, '', url.toString());

    await fetchTopicViewpoint(topicId, viewpointId);
  };

  const fetchTopicViewpoint = useCallback(async (topicId: string, viewpointId: string) => {
    setPendingViewpoint(null);
    setPendingTopicPoints(null);
    // Abrir por link de Atividade encadeia fetch do viewpoint + ativação dos urns
    // + load de cada modelo na Autodesk. Sem indicação, a área do viewer ficava
    // em branco o tempo todo e parecia travada.
    setViewpointLoading(true);

    try {
      const response = await ActivitiesApi.getTopicViewpoint(topicId, viewpointId);
      const payload = response.data?.data;

      if (!payload?.InitialViewpoint) {
        console.error('No InitialViewpoint in response');
        toastHandler.showError(t('page.viewpointNotFound'));
        setViewpointLoading(false);
        return;
      }

      const viewpointData = payload.InitialViewpoint as IInitialViewpoint;
      const topicPointsData = payload.TopicPoints as ITopicPoint[] | undefined;

      ensureModelsLoaded(viewpointData.BimFiles || null);
      setPendingViewpoint(viewpointData); // For initial loading scenario
      setPendingTopicPoints(topicPointsData || null);

      // Apply viewpoint if viewer is already initialized
      if (viewpointRestorerRef.current) {
        const areRequiredModelsLoaded = () => {
          if (!viewpointData.BimFiles || viewpointData.BimFiles.length === 0) {
            return models.length > 0;
          }

          return viewpointData.BimFiles.every((bimFile) => {
            const reference = normalizeUrn(bimFile.Reference || bimFile.Filename);
            if (!reference) { return true; }

            return models.some((model) => {
              const modelUrn = model.urn;
              return modelUrn === reference ||
                stripUrnPrefix(modelUrn) === stripUrnPrefix(reference);
            });
          });
        };

        let attempts = 0;
        const maxAttempts = 50; // 5 seconds max (50 * 100ms)

        const checkAndRestore = () => {
          const loaded = areRequiredModelsLoaded();
          if (loaded) {
            if (viewpointRestorerRef.current) {
              viewpointRestorerRef.current(viewpointData, topicPointsData);
            }
          } else if (attempts < maxAttempts) {
            attempts++;
            setTimeout(checkAndRestore, 100);
          } else {
            if (viewpointRestorerRef.current) {
              viewpointRestorerRef.current(viewpointData, topicPointsData);
            }
          }
        };

        checkAndRestore();
      }
    } catch (error) {
      console.error('Error loading topic viewpoint', error);
      toastHandler.showError(t('page.viewpointLoadError'));
      setViewpointLoading(false);
    }
  }, [ensureModelsLoaded, models, normalizeUrn, stripUrnPrefix]);

  // O overlay sai quando os modelos do viewpoint estão na cena — ou no teto de
  // tempo, para que uma conversão que nunca chega não deixe a tela coberta.
  useEffect(() => {
    if (!viewpointLoading || !pendingViewpoint) {
      return;
    }

    if (areViewpointModelsLoaded(pendingViewpoint)) {
      setViewpointLoading(false);
    }
  }, [areViewpointModelsLoaded, pendingViewpoint, viewpointLoading]);

  useEffect(() => {
    if (!viewpointLoading) {
      return;
    }

    const timeoutId = setTimeout(() => setViewpointLoading(false), 30000);
    return () => clearTimeout(timeoutId);
  }, [viewpointLoading]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const topicIdParam = searchParams.get('topicId');
    const viewpointIdParam = searchParams.get('viewpointId');

    if (topicIdParam && viewpointIdParam) {
      setViewpointRequest((current) => {
        if (current && current.topicId === topicIdParam && current.viewpointId === viewpointIdParam) {
          return current;
        }
        return { topicId: topicIdParam, viewpointId: viewpointIdParam };
      });
    }
  }, [location.search]);

  useEffect(() => {
    if (!viewpointRequest) {
      return;
    }

    const { topicId, viewpointId } = viewpointRequest;
    // Reset immediately to prevent duplicate API calls on re-renders
    setViewpointRequest(null);
    fetchTopicViewpoint(topicId, viewpointId);
  }, [viewpointRequest, fetchTopicViewpoint]);

  useEffect(() => {
    if (!pendingViewpoint) {
      return;
    }

    ensureModelsLoaded(pendingViewpoint.BimFiles || null);
  }, [ensureModelsLoaded, pendingViewpoint, viewerListDisciplines]);

  // Load activities options on mount to ensure they're available for the toolbar button
  useEffect(() => {
    dispatch(ActivitiesActions.listActivities({
      csId: Number(params.constructionSiteId),
    }));
  }, [dispatch, params.constructionSiteId]);

  useEffect(() => {
    if (loadUrn) {
      return;
    }

    // Quem roda no nosso motor não passa pela Autodesk: pedir o load travaria o
    // loadUrn (só o InternalViewer o limpa) e com ele todos os switches. Vale
    // para a urn sintética E para o `.ifc` já convertido, que tem urn de verdade
    // mas continua sendo desenhado pelo ifc-lite.
    const nextUrn = urns.find((urnValue) => (
      !isIfcUrn(urnValue)
      && !isIfcEngineUrn(urnValue)
      && !failedUrnsRef.current.has(urnValue)
      && !models.some((model) => model.urn === urnValue)
    ));

    if (nextUrn) {
      handleLoadUrn(nextUrn);
    }
  }, [handleLoadUrn, isIfcEngineUrn, loadUrn, models, urns]);

  // Viewpoint restoration now handled via initialViewpoint prop

  const isDrawerActiveModels = activeDrawer === 'models';
  const isDrawerActiveViews = activeDrawer === 'views';
  const isDrawerActiveViewPoints = activeDrawer === 'viewpoints';
  const isDrawerActivePranchas = activeDrawer === 'pranchas';
  const isDrawerActiveActivities = activeDrawer === 'activities';
  const hasUrns = urns.length > 0;

  const handleFederatedEngineError = useCallback((detail: { code?: string; message?: string }) => {
    Mixpanel.track({
      name: 'COORDLY_3D_VIEWER_FALLBACK',
      props: { reason: detail?.code || 'engine-error', at: 'federated', message: detail?.message },
    });
    toastHandler.showError(i18n.t('messages:toasts.bim3dLoadError'));
  }, []);

  // Modelo IFC que não entrou na cena: desligar o urn tira o switch (que é
  // `urns.includes(...)`) e limpa a árvore/views dele. O toast já veio pelo
  // `onEngineError` — aqui é só devolver a lista ao estado anterior.
  const handleIfcModelLoadFailed = useCallback((urn: string) => {
    handleModelLoadError(urn);
    dispatch(FederatedViewerActions.setUrns(urn));
  }, [dispatch, handleModelLoadError]);

  // Gate da inclusão (só no fluxo do usuário). O bloqueio é de mão única, e é o
  // que sustenta a retrocompatibilidade enquanto o federado ainda mistura os
  // dois mundos:
  //
  // - cena vazia: qualquer arquivo entra, e é ele quem escolhe o motor;
  // - sessão da Autodesk (começou por `.rvt`, `.nwd`…): tudo continua entrando,
  //   IFC inclusive — ela desenha IFC desde sempre, pela urn convertida;
  // - sessão nossa (começou por `.ifc`): só `.ifc`. O ifc-lite ignora qualquer
  //   outra extensão, então o switch ligaria sem nada aparecer na cena.
  //
  // Antes o teste era pela flag, e com ela ligada nem um `.rvt` já convertido
  // entrava, mesmo com a cena vazia. Devolve se a ativação foi aceita: só então
  // a lista trata o clique como render (Mixpanel, load na Autodesk). Não há teto
  // de tamanho: o parse do IFC é sempre no server.
  const gatedActiveUrns = useCallback((urn: string, extension: string): boolean => {
    // Desligar, e a cena vazia, nunca dependem do motor: quem entrou na sessão
    // precisa poder sair dela, e a primeira ativação é quem escolhe o motor.
    // `pending` conta como IFC: o 1º urn é `.ifc` e só falta o probe de WebGPU.
    const sessionIsIfc = sessionEngine === 'ifc' || sessionEngine === 'pending';
    if (!urns.includes(urn)
      && sessionIsIfc
      && (extension || '').toLowerCase() !== COORDLY_3D_VIEWER_EXTENSION) {
      toastHandler.showInfo(t('page.onlyIfcInSession'));
      return false;
    }
    handleActiveUrns(urn, extension);
    return true;
  }, [urns, sessionEngine, handleActiveUrns, t]);
  const hasViews = views.length > 0;
  const hasViewpoints = viewpoints.length > 0;
  const hasPranchas = pranchas.length > 0;

  const isPopulatedDisciplinesChildrensLoading = ((loadingDiscipline || loadingFiles)
    || ((!isDrawerActiveModels && !isDrawerActiveActivities)
      && !hasUrns
    ));

  const searchViewerInputPlaceholder = ((!isDrawerActiveModels && !isDrawerActiveActivities)
    ? t('page.searchPlaceholderDrawer', { drawer: activeDrawer })
    : (
      activeDisciplineId
        ? t('page.searchPlaceholderModel')
        : t('page.searchPlaceholderDiscipline')
    ));

  return (
    <div className={styles['wrapper']}>
      <TabsShell
        ConstructionSiteId={params.constructionSiteId}
        active={PathActiveEnum.viewer}
        loading={loadingDiscipline || loadingFiles}
      />
      <ViewerSignalR constructionSiteId={Number(params.constructionSiteId)} />
      <FederatedDrawer
        activeButton={activeDrawer}
        onActiveOption={handleActiveList}
        visible={drawerFederatedVisible}
        onClose={closeDrawerFederated}
        onShow={showDrawerFederated}
      >
        <SearchViewerInput
          value={searchDiscipline || searchDisciplineFiles || searchItens}
          uploadButton={!isDrawerActiveModels || !isDrawerActiveActivities}
          onSearch={handleSearch}
          firstLoading={isPopulatedDisciplinesChildrensLoading}
          inputPlaceholder={searchViewerInputPlaceholder}
          onUploadFile={uploadFileViewer}
          onMaletaFile={handleMaletaFileViewer}
          isDebounce={false}
          limitRender={userInfo?.UserStoreItem.MaxQtyRenderedFiles ||
            userInfo?.UserStoreItem.StorePlan?.MaxQtyRenderedFiles}
          activeRender={userLimitsActive?.QtyRenderedFiles}
          onShowCTARender={handleShowCTARender}
          onLimitBarProgress={handleShowProgressLimitRender}
        />
        {(loadingDiscipline || loadingFiles) ? (
          <LoadingDrawer multiple={6} />
        ) : (
          <>
            {isDrawerActiveModels && (
              (filteredEmptyDisciplines.length > 0) ? (
                <Models
                  activeDisciplineId={activeDisciplineId}
                  openDiscipline={openDiscipline}
                  backDisciplines={backDisciplines}
                  disciplineList={filteredEmptyDisciplines}
                  childrenDisciplinesActive={childrenActiveDisciplines}
                  searchDiscipline={searchDiscipline}
                  searchDisciplineFiles={searchDisciplineFiles}
                  loadUrn={loadUrn}
                  onActiveUrns={gatedActiveUrns}
                  accessViewerTab={accessViewerTab}
                  urns={urns}
                  onCTAMultipleRender={handleOpenCTAModal}
                  modelsTree={modelsTree}
                  onToggleNode={handleToogleNode}
                  hideNodes={hideNodes}
                  onToggleSelectNode={handleToogleSelectNode}
                  selectedNode={selectedNode}
                  constructionSiteId={Number(params.constructionSiteId)}
                  api={obra?.Api}
                  userInfo={userInfo}
                  ifcEngineReady={ifcEngineReady}
                  sessionEngine={sessionEngine}
                  ifcLoadingUrn={ifcLoadingUrn}
                  ifcTrees={ifcTrees}
                  ifcHandleRef={ifcHandleRef}
                  ifcSelectionSubscribe={subscribeIfcSelection}
                  dispatch={dispatch}
                />
              ) : (
                <EmptyModels
                  onPlay={() => handleOpenVideoModal()}
                  isModelActive
                />
              )
            )}
            {isDrawerActiveViews && (
              accessViewerTab ? (
                <>
                  {(hasUrns && hasViews) && (
                    <ModelsViews
                      listItems={views}
                      searchItems={searchItens}
                      childrenNames={childrenActiveUrnsNames}
                      activeDrawer={activeDrawer}
                      toggleView={toggleView}
                      onToggleView={handleToogleView}
                    />
                  )}
                  {(!hasUrns || (hasUrns && !hasViews)) && (
                    <EmptyModels
                      onPlay={() => handleActiveList('models')}
                      activeDrawer={activeDrawer}
                      hasUrns={hasUrns}
                    />
                  )}
                </>
              ) : (
                <CTAUpgradeAccessViewer
                  title={activeDrawer}
                  role={userInfo?.CurrentRoleFk}
                  onConfirm={handleUpgradePlan}
                />
              )
            )}
            {isDrawerActiveViewPoints && (
              accessViewerTab ? (
                <>
                  {(hasUrns && hasViewpoints) && (
                    <ModelsViewpoints
                      listItems={viewpoints}
                      searchItems={searchItens}
                      childrenNames={childrenActiveUrnsNames}
                      activeDrawer={activeDrawer}
                      toggleViewCamera={toggleViewCamera}
                      onToggleViewCamera={handleToogleViewCamera}
                    />
                  )}
                  {(!hasUrns || (hasUrns && !hasViewpoints)) && (
                    <EmptyModels
                      onPlay={() => handleActiveList('models')}
                      activeDrawer={activeDrawer}
                      hasUrns={hasUrns}
                    />
                  )}
                </>
              ) : (
                <CTAUpgradeAccessViewer
                  title={activeDrawer}
                  role={userInfo?.CurrentRoleFk}
                  onConfirm={handleUpgradePlan}
                />
              )
            )}
            {isDrawerActivePranchas && (
              accessViewerTab ? (
                <>
                  {(hasUrns && hasPranchas) && (
                    <ModelsPranchas
                      listItems={pranchas}
                      searchItems={searchItens}
                      childrenNames={childrenActiveUrnsNames}
                      activeDrawer={activeDrawer}
                      togglePrancha={togglePrancha}
                      onTogglePrancha={handleTooglePrancha}
                    />
                  )}
                  {(!hasUrns || (hasUrns && !hasPranchas)) && (
                    <EmptyModels
                      onPlay={() => handleActiveList('models')}
                      activeDrawer={activeDrawer}
                      hasUrns={hasUrns}
                    />
                  )}
                </>
              ) : (
                <CTAUpgradeAccessViewer
                  title={activeDrawer}
                  role={userInfo?.CurrentRoleFk}
                  onConfirm={handleUpgradePlan}
                />
              )
            )}
            {isDrawerActiveActivities && (
              activityViewpointFeature ? (
                <ActivitiesList
                  constructionSiteId={Number(params.constructionSiteId)}
                  onCreateActivity={handleCreateActivity}
                  onOpenActivity={handleOpenActivity}
                />
              ) : (
                <ActivitiesDemo />
              )
            )}
          </>
        )}
      </FederatedDrawer>
      <div className={styles['mainWrapper']}>
        <div className={drawerFederatedVisible ? styles['viewerWrapperDrawerOpen'] : styles['viewerWrapper']}>
          {viewpointLoading && (
            <ViewerLoadingOverlay message={t('page.loadingActivityModel')} />
          )}
          {!hasUrns ? (
            <EmptyViewer
              isDrawerVisible={drawerFederatedVisible}
            />
          ) : sessionEngine === 'pending' ? (
            <EmptyViewer
              isDrawerVisible={drawerFederatedVisible}
            />
          ) : sessionEngine === 'ifc' ? (
            <FederatedIfcViewer
              urns={urns}
              viewerListDisciplines={viewerListDisciplines}
              constructionSiteId={Number(params.constructionSiteId)}
              drawerOpen={drawerFederatedVisible}
              onEngineError={handleFederatedEngineError}
              onModelLoadFailed={handleIfcModelLoadFailed}
              onLoadingUrnChange={setIfcLoadingUrn}
              onModelTree={handleIfcModelTree}
              onSelectionChanged={handleIfcSelectionChanged}
              onHandleReady={(handle) => { ifcHandleRef.current = handle; }}
            />
          ) : (
            <InternalViewer
              loadUrn={loadUrn}
              drawerOpen={drawerFederatedVisible}
              toggleNode={toggleHideShowNode}
              togglePrancha={togglePrancha}
              toggleSelectNode={toggleSelectNode}
              toggleViewCamera={toggleViewCamera}
              toggleView={toggleView}
              initialViewpoint={pendingViewpoint}
              initialTopicPoints={pendingTopicPoints}
              visibilityOptions={visibilityOptions}
              priorityOptions={priorityOptions}
              priorityNoBgOptions={priorityNoBgOptions}
              typeOptions={typeOptions}
              stageOptions={stageOptions}
              pointsOptions={pointsOptions}
              communicationOptions={communicationOptions}
              userOptions={userOptions}
              disciplineOptions={disciplineOptions}
              disciplines={disciplines}
              stages={topicStages}
              allowOnlyStagesLinkedToDisciplinesInTopics={allowOnlyStagesLinkedToDisciplinesInTopics}
              createStageLoading={createStageLoading}
              activityViewpointFeature={activityViewpointFeature}
              onLoadUrn={handleLoadUrn}
              onActiveUrns={handleActiveUrns}
              onToggleNode={handleToogleNode}
              onToggleSelectNode={handleToogleSelectNode}
              onTogglePrancha={handleTooglePrancha}
              onToggleViewCamera={handleToogleViewCamera}
              onToggleView={handleToogleView}
              onClearAllToggles={handleClearAllToogles}
              onModelLoadError={handleModelLoadError}
              onCreateActivityHandlerReady={handleCreateActivityHandlerReady}
              onRegisterViewpointRestorer={(handler: (viewpoint: IInitialViewpoint, topicPoints?: ITopicPoint[]) => void) => {
                viewpointRestorerRef.current = handler;
                setRestorerReady(true);
              }}
            />
          )}
        </div>
      </div>
      <input
        id='uploadFileViewer'
        type='file'
        hidden
        accept={isCoordly3DViewerEnabled ? '.ifc' : undefined}
        onChange={(e) => {
          handleUploadFileViewer(e.currentTarget.files);
          e.currentTarget.value = '';
        }}
      />
      {showVideo && (
        <VideoEx
          visible={showVideo}
          url={demoVideoViewer}
          onCancel={() => handleCloseVideoModal()}
        />
      )}
      {showSelectUploadPath && (
        <FileDestination
          visible={showSelectUploadPath}
          disciplineList={filteredEmptyDisciplines}
          onCancel={() => cancelUploadFileViewer()}
          file={upfileViewer}
          currentDiscipline={activeDisciplineId}
          constructionSiteId={Number(params.constructionSiteId)}
          fromMaleta={selectUploadPathFromMaleta}
        />
      )}
      {showCTAViewerUpgrade && (
        <CTAViewerUpgrade
          visible={showCTAViewerUpgrade}
          onCancel={() => setShowCTAViewerUpgrade(false)}
          onConfirm={handleUpgradePlan}
          role={userInfo?.CurrentRoleFk}
        />
      )}
      {showCTAMultipleModelsUpgrade && (
        <CTAMultipleModels
          onConfirm={handleUpgradePlan}
          onClose={handleCloseCTAModal}
          role={userInfo?.CurrentRoleFk}
          indexList={indexList}
        />
      )}
      {showModalProgressLimitRender && (
        <ProgressRenderBar
          visible={showModalProgressLimitRender}
          maxLimits={userInfo?.UserStoreItem.MaxQtyRenderedFiles ||
            userInfo?.UserStoreItem.StorePlan?.MaxQtyRenderedFiles}
          value={userLimitsActive?.QtyRenderedFiles || userInfo?.QtyRenderedFiles || 0}
          onCancel={handleCloseProgressLimitRender}
          onConfirmRender={uploadFileViewer}
          inFederatedViewer
        />
      )}
      {showActivityModal && (
        <ShowCreateActivity
          visible={showActivityModal}
          loadingExternalLink={false}
          loading={activityLoading}
          loadingUpdate={isLoadingUpdate}
          loadingDelete={isLoadingDelete}
          editStage={editStage}
          updateActivity={selectedActivity}
          csId={Number(params.constructionSiteId)}
          title={t('page.activityTitle')}
          topicStatus={topicStatus}
          topicStages={topicStages}
          topicPoints={topicPoints}
          topicCommunicationType={topicCommunicationType}
          visibilityOptions={visibilityOptions}
          priorityOptions={priorityOptions}
          priorityNoBgOptions={priorityNoBgOptions}
          statusOptions={statusOptions}
          typeOptions={typeOptions}
          stageOptions={stageOptions}
          pointsOptions={pointsOptions}
          communicationOptions={communicationOptions}
          userOptions={userOptions}
          disciplineOptions={disciplineOptions}
          disciplines={disciplines}
          allowOnlyStagesLinkedToDisciplinesInTopics={allowOnlyStagesLinkedToDisciplinesInTopics}
          createStageLoading={createStageLoading}
          updateStatus={updateStatus}
          getOld={getOld}
          newActivityGuid={newActivityGuid}
          userInfo={userInfo}
          onOpenShowActivity={handleOpenShowActivity}
          onOpenViewpoint={handleOpenActivityViewpoint}
          onCancel={handleCloseActivity}
        />
      )}
    </div>
  );
};

const mapState = (state: IGlobalReducerState) => ({
  ...state.viewer,
  ...state.federatedViewer,
  userInfo: state.auth.userInfo,
  planFree: state.auth.planFree,
  userLimitsActive: state.auth.userLimitsActive,
  accessViewerTab: state.auth.userInfo?.UserStoreItem.StorePlan?.AccessViewerTab,
  isLoading: state.federatedViewer.isLoading,
});

const connector = connect(mapState);

type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = PropsFromRedux;

export default connector(FederatedViewer);
